// De blokken van het eenscherm (herbestelronde 15-09, mock goedgekeurd).
//
// Eén scherm waarop de klant alles ziet en in één tik betaalt: geen
// aparte controlepagina meer. Elk blok hieronder toont uitsluitend wat
// de offertedienst teruggaf — er wordt hier niets gerekend, ook geen
// optelling. Dat is de kern van deze ronde: zodra de frontend zelf zou
// rekenen, kan het bedrag op de knop afwijken van het bedrag dat de
// server straks bij MultiSafepay neerlegt.
//
// De wachtstand loopt overal langs dezelfde `bezig`-vlag: Bezorging en
// Totaal worden grijs (skelet), de betaalknop gaat uit en zegt "Even
// geduld…". Een blok dat tijdens het verversen zijn oude bedrag laat
// staan doet dat bewust zichtbaar grijs — leegspringen is onrustiger, en
// de knop kan toch niet ingedrukt worden.

import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Loader2 } from "lucide-react";

import type {
  HerbestelBetaalmethode,
  HerbestelOfferteAntwoord,
} from "@/types/api";
import {
  GRIJS,
  GROEN,
  ORANJE,
  TINT,
  TINT_RAND,
  euro,
} from "./herbestelStijl";

// Dezelfde binnenmarge als de productkaarten in de linkerkolom
// (p-5): met een smallere marge rechts liepen de twee kolommen op
// desktop zichtbaar uit elkaar (JP, 15-09).
const KAART = "rounded-xl border border-gray-200 bg-white p-5";

/** Het blok is grijs en niet aanklikbaar zolang de offerte ververst. */
function skelet(bezig: boolean): string {
  return bezig ? "opacity-50 transition-opacity" : "transition-opacity";
}

/**
 * De bezorgregel bovenaan, met de knop Wijzig.
 *
 * Het adres staat vet op een lichte tint en Wijzig is een knop-achtige
 * link: op de mock is dit de regel waar de klant als eerste naar kijkt,
 * en tussen louter witte kaarten viel hij weg (JP, 15-09).
 *
 * Toont het volledige bezorgadres (JP-besluit 15-09). Dat herziet punt
 * (4) van het besluit van 03-09, dat alleen de woonplaats toeliet: dit
 * scherm ís de checkout, en je kunt niet controleren waar een bestelling
 * heen gaat zonder te zien waar hij heen gaat. De rest van dat besluit
 * blijft: nooit een telefoonnummer, nooit betaalgegevens, en de link
 * waarmee je hier komt is kortlevend, uniek en na betaling dood.
 */
export function Adresregel({
  regel,
  herkomst,
  bezig,
  opWijzig,
}: {
  regel: string | null;
  herkomst: "klant" | "ander";
  bezig: boolean;
  opWijzig: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="adresregel"
      data-herkomst={herkomst}
      className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-4 py-3"
      style={{ background: TINT, borderColor: TINT_RAND }}
    >
      <span className="min-w-0 flex-1 text-[14px] leading-snug text-[#333333]">
        <span>{t("herbestel.bezorgen_op")}</span>{" "}
        {regel ? (
          <span
            data-testid="adresregel-tekst"
            className="font-bold text-gray-900"
          >
            {regel}
          </span>
        ) : (
          <span className="text-gray-500">
            {t("herbestel.adres_nog_niet")}
          </span>
        )}
      </span>
      <button
        type="button"
        data-testid="adres-wijzig"
        disabled={bezig}
        onClick={opWijzig}
        className="shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-[13.5px] font-semibold text-blue-600 hover:border-gray-400 disabled:opacity-60"
      >
        {t("herbestel.adres_wijzig")}
      </button>
    </div>
  );
}

/**
 * Bezorging: de opties uit de offerte, met de standaardoptie voorgekozen.
 *
 * Alleen thuisbezorging — afhaalpunten filtert de server er al uit.
 * Achter elke optie staat het **hele** bedrag: "€ 5,95", "€ 7,90", en
 * "Gratis" (groen) alleen als hij echt nul is. Tot 17-09 stond er bij
 * een duurdere optie "+ € 1,95"; dat las als een toeslag bovenop het
 * totaal in plaats van als de prijs van die bezorging (JP, 17-09). Het
 * bedrag komt altijd uit de offerte — het scherm rekent niets.
 *
 * Biedt Lightspeed voor dit mandje maar één manier van bezorgen, dan
 * blijft het blok staan maar vervalt de radio: één bolletje dat altijd
 * aan staat is geen keuze, en het suggereert dat er iets te kiezen valt
 * dat er niet is. De regel zelf blijft, want de klant moet wel zien
 * hoe en voor hoeveel er bezorgd wordt.
 *
 * Zonder opties hangt het van de stand af (hotfix 17-09): tijdens het
 * ophalen staat er "Bezorgopties ophalen…", en pas als de offerte écht
 * mislukte de foutmelding. Daarvóór stond die foutmelding er ook
 * tijdens het laden — het eerste wat de klant las was dat het niet
 * lukte, terwijl er gewoon nog gewerkt werd.
 */
export function Bezorgblok({
  offerte,
  bezig,
  fout,
  aangepast,
  opKies,
}: {
  offerte: HerbestelOfferteAntwoord | null;
  bezig: boolean;
  /** De offerte is echt mislukt (na de stille herkansing). */
  fout: boolean;
  /** De gekozen vervoerder bestaat niet meer; de keuze is verzet. */
  aangepast: boolean;
  opKies: (id: string, titel: string) => void;
}) {
  const { t } = useTranslation();
  const opties = offerte?.opties ?? [];
  const keuze = opties.length > 1;
  const bedrag = (optie: (typeof opties)[number]) =>
    optie.gratis ? (
      <span style={{ color: GROEN }}>{t("herbestel.gratis")}</span>
    ) : (
      <span className="text-gray-900">{euro(optie.prijs_cents)}</span>
    );
  return (
    <div data-testid="bezorgblok" className={`${KAART} ${skelet(bezig)}`}>
      <p className="text-[15px] font-bold text-gray-900">
        {t("herbestel.bezorging_titel")}
      </p>
      {opties.length === 0 ? (
        fout ? (
          <p
            data-testid="bezorging-fout"
            className="mt-2 text-[13.5px] text-gray-500"
          >
            {t("herbestel.bezorging_leeg")}
          </p>
        ) : bezig ? (
          <p
            role="status"
            data-testid="bezorging-laden"
            className="mt-2 text-[13.5px] text-gray-500"
          >
            {t("herbestel.bezorging_laden")}
          </p>
        ) : null
      ) : !keuze ? (
        // Eén manier van bezorgen: geen radiogroep, geen bol. Wel de
        // regel zelf, met hetzelfde bedrag op dezelfde plek.
        <div
          data-testid={`bezorgoptie-${opties[0].id}`}
          data-enige="ja"
          className="mt-2.5 flex w-full items-center gap-3 text-left"
        >
          <span className="min-w-0 flex-1 text-[14px] font-bold leading-snug text-gray-900">
            {opties[0].titel}
          </span>
          <span className="shrink-0 text-[13.5px] font-semibold">
            {bedrag(opties[0])}
          </span>
        </div>
      ) : (
        <div
          role="radiogroup"
          aria-label={t("herbestel.bezorging_titel")}
          className="mt-2.5 grid gap-2"
        >
          {opties.map((optie) => {
            const actief = optie.id === offerte?.gekozen_optie;
            return (
              <button
                key={optie.id}
                type="button"
                role="radio"
                aria-checked={actief}
                data-testid={`bezorgoptie-${optie.id}`}
                disabled={bezig}
                onClick={() => opKies(optie.id, optie.titel)}
                className={`flex w-full items-center gap-3 rounded-[10px] border-2 bg-white px-3.5 py-3 text-left disabled:cursor-not-allowed disabled:opacity-60 ${
                  actief ? "" : "border-gray-200"
                }`}
                style={actief ? { borderColor: ORANJE } : undefined}
              >
                <span
                  aria-hidden
                  className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${
                    actief ? "" : "border-gray-300"
                  }`}
                  style={actief ? { borderColor: ORANJE } : undefined}
                >
                  {actief && (
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ background: ORANJE }}
                    />
                  )}
                </span>
                <span className="min-w-0 flex-1 text-[14px] font-bold leading-snug text-gray-900">
                  {optie.titel}
                </span>
                <span className="shrink-0 text-[13.5px] font-semibold">
                  {bedrag(optie)}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {/* De klant had een vervoerder gekozen die Lightspeed voor dit
          mandje niet meer aanbiedt (hotfix 17-09). De keuze staat nu op
          de standaardmethode en dat hoort hij te lezen — stil een
          andere bezorging tonen is precies de fout van 17-09. */}
      {aangepast && (
        <p
          role="status"
          data-testid="bezorging-aangepast"
          className="mt-2.5 text-[12.5px] font-semibold leading-relaxed text-gray-700"
        >
          {t("herbestel.bezorging_aangepast")}
        </p>
      )}
      {/* Eén belofteregel, niet per vervoerder een bezorgdatum: een
          datum per optie zou een belofte zijn die we hier niet kunnen
          nakomen (dat hangt aan het afhaalmoment, niet aan de keuze). */}
      <p className="mt-2.5 text-[12.5px] leading-relaxed text-gray-500">
        {t("herbestel.bezorg_belofte")}
      </p>
    </div>
  );
}

/**
 * Betalen: iDEAL staat voorgekozen, de rest zit achter één link.
 *
 * De keuze verandert het totaal (Billink kost 1,99), dus elke klik hier
 * ververst de offerte — het bedrag op de knop blijft daarmee altijd het
 * bedrag dat de klant echt betaalt.
 */
export function Betaalblok({
  methodes,
  gekozen,
  open,
  bezig,
  achterafWeg,
  opKies,
  opOpen,
}: {
  methodes: HerbestelBetaalmethode[];
  gekozen: string;
  open: boolean;
  bezig: boolean;
  /** Achteraf betalen is weggevallen omdat er een ander adres staat. */
  achterafWeg: boolean;
  opKies: (code: string) => void;
  opOpen: () => void;
}) {
  const { t } = useTranslation();
  if (methodes.length === 0) return null;
  const voorkeur =
    methodes.find((m) => m.code === gekozen) ?? methodes[0];
  const zichtbaar = open ? methodes : [voorkeur];
  return (
    <div data-testid="betaalblok" className={KAART}>
      <p className="text-[15px] font-bold text-gray-900">
        {t("herbestel.betaal_titel")}
      </p>
      <div
        role="radiogroup"
        aria-label={t("herbestel.betaal_titel")}
        className="mt-2.5 grid gap-2"
      >
        {zichtbaar.map((methode) => {
          const actief = methode.code === voorkeur.code;
          return (
            <button
              key={methode.code}
              type="button"
              role="radio"
              aria-checked={actief}
              data-testid={`betaalmethode-${methode.code}`}
              disabled={bezig}
              onClick={() => opKies(methode.code)}
              className={`flex w-full items-center gap-3 rounded-[10px] border-2 bg-white px-3.5 py-3 text-left disabled:cursor-not-allowed disabled:opacity-60 ${
                actief ? "" : "border-gray-200"
              }`}
              style={actief ? { borderColor: ORANJE } : undefined}
            >
              <span
                aria-hidden
                className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${
                  actief ? "" : "border-gray-300"
                }`}
                style={actief ? { borderColor: ORANJE } : undefined}
              >
                {actief && (
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ background: ORANJE }}
                  />
                )}
              </span>
              <img
                src={methode.icoon_url}
                alt=""
                aria-hidden
                loading="lazy"
                className="h-6 w-10 shrink-0 object-contain"
              />
              <span className="min-w-0 flex-1 text-[14px] font-bold leading-snug text-gray-900">
                {methode.naam}
              </span>
              {methode.toeslag_cents > 0 && (
                <span
                  data-testid={`toeslag-${methode.code}`}
                  className="shrink-0 text-[13px] font-semibold text-gray-600"
                >
                  + {euro(methode.toeslag_cents)}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {achterafWeg && (
        <p
          data-testid="achteraf-regel"
          className="mt-2 text-[12.5px] leading-relaxed text-gray-500"
        >
          {t("herbestel.achteraf_alleen_eigen_adres")}
        </p>
      )}
      {!open && methodes.length > 1 && (
        <button
          type="button"
          data-testid="andere-betaalmethode"
          disabled={bezig}
          onClick={opOpen}
          className="mt-2.5 flex items-center gap-1 rounded-sm text-[13.5px] font-medium text-blue-600 underline-offset-2 hover:underline disabled:opacity-60"
        >
          {t("herbestel.andere_betaalmethode")}
          <ChevronDown className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

/**
 * Kortingscode: ingeklapt als link, uitgeklapt als veld.
 *
 * Toepassen ververst de offerte; het totaal komt daarna uit Lightspeed.
 * Liep de code niet mee, dan zegt het scherm dat eerlijk in plaats van
 * hem stil te laten vallen — een code die "niets doet" zonder uitleg is
 * precies waar een klant de klantenservice voor belt.
 */
export function Kortingsblok({
  open,
  waarde,
  toegepast,
  werkt,
  bezig,
  opOpen,
  opWijzig,
  opToepassen,
  opWissen,
}: {
  open: boolean;
  waarde: string;
  toegepast: string | null;
  werkt: boolean;
  bezig: boolean;
  opOpen: () => void;
  opWijzig: (waarde: string) => void;
  opToepassen: () => void;
  opWissen: () => void;
}) {
  const { t } = useTranslation();
  if (!open) {
    return (
      <button
        type="button"
        data-testid="kortingscode-open"
        onClick={opOpen}
        className="rounded-sm text-left text-[13.5px] font-medium text-blue-600 underline-offset-2 hover:underline"
      >
        {t("herbestel.kortingscode_vraag")}
      </button>
    );
  }
  return (
    <form
      data-testid="kortingsblok"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        opToepassen();
      }}
      className={KAART}
    >
      <label
        htmlFor="herbestel-kortingscode"
        className="block text-[13px] font-semibold text-[#333333]"
      >
        {t("herbestel.kortingscode_label")}
      </label>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <input
          id="herbestel-kortingscode"
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          value={waarde}
          disabled={bezig}
          onChange={(e) => opWijzig(e.target.value)}
          // 16px tegen de iOS-zoom, zie het adresformulier.
          className="min-w-[160px] flex-1 rounded-[10px] border border-gray-300 bg-white px-3.5 py-2.5 text-[16px] font-normal text-gray-900 outline-none focus:border-gray-400 disabled:opacity-60"
        />
        <button
          type="submit"
          data-testid="kortingscode-toepassen"
          disabled={bezig || waarde.trim().length === 0}
          className="rounded-[10px] border-2 border-gray-300 bg-white px-4 py-2.5 text-[14px] font-bold text-gray-700 disabled:opacity-60"
        >
          {t("herbestel.kortingscode_toepassen")}
        </button>
      </div>
      {toegepast && werkt && (
        <p
          data-testid="kortingscode-gelukt"
          className="mt-2 flex items-center gap-1.5 text-[13px] font-semibold"
          style={{ color: GROEN }}
        >
          <Check className="h-4 w-4" aria-hidden />
          {t("herbestel.kortingscode_gelukt", { code: toegepast })}
          <button
            type="button"
            data-testid="kortingscode-wissen"
            disabled={bezig}
            onClick={opWissen}
            className="ml-1 rounded-sm text-[12.5px] font-medium text-blue-600 underline-offset-2 hover:underline disabled:opacity-60"
          >
            {t("herbestel.kortingscode_wissen")}
          </button>
        </p>
      )}
      {!werkt && (
        <p
          role="alert"
          data-testid="kortingscode-fout"
          className="mt-2 text-[13px] font-semibold text-red-600"
        >
          {t("herbestel.kortingscode_werkt_niet")}
        </p>
      )}
    </form>
  );
}

/**
 * Het totaal plus de betaalknop.
 *
 * De knop draagt altijd het bedrag van de laatste offerte. Is er geen
 * verse offerte (aan het verversen, of de verversing mislukte), dan
 * staat hij uit — zo kan een oud totaal nooit afgerekend worden, en de
 * server toetst dat daarnaast nog eens aan de vingerafdruk.
 *
 * De btw-regel verschijnt alleen als de offerte een btw-bedrag meegaf;
 * de server laat hem weg zodra hij niet sluit met het totaal, zodat er
 * nooit een kolom staat die niet optelt.
 */
export function Totaalblok({
  offerte,
  bezig,
  fout,
  betaalBezig,
  geenProducten,
  kanBetalen,
  opBetalen,
  opOpnieuw,
}: {
  offerte: HerbestelOfferteAntwoord | null;
  bezig: boolean;
  fout: boolean;
  betaalBezig: boolean;
  /** Niets gekozen: dan zegt de knop dat, en niet "even geduld". */
  geenProducten: boolean;
  kanBetalen: boolean;
  opBetalen: () => void;
  opOpnieuw: () => void;
}) {
  const { t } = useTranslation();
  const uit = !kanBetalen || bezig || fout || betaalBezig;
  return (
    <div data-testid="totaalblok" className={KAART}>
      <div className={skelet(bezig)}>
        <dl className="grid gap-1.5 text-[14px] text-[#333333]">
          <div className="flex items-baseline justify-between gap-4">
            <dt>{t("herbestel.totaal_artikelen")}</dt>
            <dd className="tabular-nums font-semibold text-gray-900">
              {euro(offerte?.artikelen_cents ?? null)}
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <dt>{t("herbestel.totaal_bezorging")}</dt>
            <dd className="tabular-nums font-semibold">
              {offerte && offerte.bezorging_cents === 0 ? (
                <span style={{ color: GROEN }}>
                  {t("herbestel.gratis")}
                </span>
              ) : (
                <span className="text-gray-900">
                  {euro(offerte?.bezorging_cents ?? null)}
                </span>
              )}
            </dd>
          </div>
          {offerte != null && offerte.korting_cents > 0 && (
            <div
              data-testid="totaal-korting"
              className="flex items-baseline justify-between gap-4"
            >
              <dt>{t("herbestel.totaal_korting")}</dt>
              <dd
                className="tabular-nums font-semibold"
                style={{ color: GROEN }}
              >
                − {euro(offerte.korting_cents)}
              </dd>
            </div>
          )}
          {offerte?.btw_cents != null && (
            <div
              data-testid="totaal-btw"
              className="flex items-baseline justify-between gap-4 text-[13px] text-gray-500"
            >
              <dt>{t("herbestel.totaal_btw")}</dt>
              <dd className="tabular-nums">{euro(offerte.btw_cents)}</dd>
            </div>
          )}
          <div className="mt-1 flex items-baseline justify-between gap-4 border-t border-gray-200 pt-2.5">
            <dt className="text-[15px] font-bold text-gray-900">
              {t("herbestel.totaal_totaal")}
            </dt>
            <dd className="flex items-baseline gap-2">
              {/* Nooit een stille wachtstand, en nooit een leeg scherm:
                  tijdens het bijwerken blijft het laatst bevestigde
                  bedrag staan met deze aanduiding ernaast (15-09, derde
                  ronde). Het staat bewust hier en niet als losse regel
                  eronder: de klant kijkt naar het bedrag, dus daar hoort
                  te staan dat het nog niet vast is. */}
              {bezig && !fout && (
                <span
                  role="status"
                  data-testid="offerte-bezig"
                  className="flex items-center gap-1 text-[12.5px] font-medium text-gray-500"
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  {t("herbestel.offerte_bijwerken")}
                </span>
              )}
              <span className="tabular-nums text-[19px] font-bold text-gray-900">
                {euro(offerte?.totaal_cents ?? null)}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      {fout && (
        <div
          role="alert"
          data-testid="offerte-fout"
          className="mt-3 rounded-[10px] border border-red-200 bg-red-50 px-3.5 py-3"
        >
          <p className="text-[13.5px] font-semibold text-red-700">
            {t("herbestel.offerte_fout")}
          </p>
          <button
            type="button"
            data-testid="offerte-opnieuw"
            onClick={opOpnieuw}
            className="mt-2 rounded-[10px] border-2 border-red-300 bg-white px-4 py-2 text-[13.5px] font-bold text-red-700"
          >
            {t("herbestel.offerte_opnieuw")}
          </button>
        </div>
      )}

      <button
        type="button"
        data-testid="betaalknop"
        disabled={uit}
        onClick={opBetalen}
        className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-[10px] px-6 text-[15px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-80"
        style={{ background: uit ? GRIJS : ORANJE }}
      >
        {betaalBezig ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            {t("herbestel.betaal_bezig")}
          </>
        ) : geenProducten ? (
          // Niets gekozen is geen wachtstand: dan zegt de knop zelf wat
          // er moet gebeuren, in plaats van te suggereren dat er iets
          // aan het laden is.
          t("herbestel.kies_minstens")
        ) : bezig || !kanBetalen ? (
          t("herbestel.even_geduld")
        ) : (
          t("herbestel.betalen_bedrag", {
            bedrag: euro(offerte?.totaal_cents ?? null),
          })
        )}
      </button>
      <p className="mt-2.5 text-[12.5px] leading-relaxed text-gray-500">
        <span className="mr-1 font-bold" style={{ color: GROEN }}>
          ✓
        </span>
        {t("herbestel.vertrouwensregel")}
      </p>
    </div>
  );
}
